import { EMPTY, exhaustMap, map, Observable, pipe } from 'rxjs';
import { inject } from '@angular/core';
import { patchState, signalStoreFeature, withMethods, withState } from '@ngrx/signals';
import { Country } from '@shared/models/country.model';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { AssessmentsApiService } from '../services/assessments-api.service';
import { AssessmentUser } from '../models/assessment-user.model';
import { SensitivityLevelApiService } from '../services/sensitivity-level-api.service';
import { MainDriverApiService } from '../services/main-driver-api.service';
import { UserApiService } from '../services/user-api.service';
import { AssessmentsDialogFilter } from '../models/assessment-filter.model';
import { DictionaryDataApiService } from '@shared/services/dictionary-data-api.service';

type AllAssessmentsFilterDataState = {
  countries: Country[];
  countriesLoading: boolean;
  industryNames: string[];
  industryNamesLoading: boolean;
  companyNames: string[];
  companyNamesLoading: boolean;
  projectNames: string[];
  projectNamesLoading: boolean;
  dealIds: string[];
  dealIdsLoading: boolean;
  underwritingSources: string[];
  underwritingSourcesLoading: boolean;
  sensitivityLevels: string[];
  sensitivityLevelsLoading: boolean;
  mainDrivers: string[];
  mainDriversLoading: boolean;
  assessmentCreators: AssessmentUser[];
  assessmentCreatorsLoading: boolean;
  businessUnits: string[];
  businessUnitsLoading: boolean;
  legalEntities: string[];
  legalEntitiesLoading: boolean;

  selectedDialogFilter: AssessmentsDialogFilter;
};

const initialState: AllAssessmentsFilterDataState = {
  countries: [],
  countriesLoading: false,
  industryNames: [],
  industryNamesLoading: false,
  companyNames: [],
  companyNamesLoading: false,
  projectNames: [],
  projectNamesLoading: false,
  dealIds: [],
  dealIdsLoading: false,
  sensitivityLevels: [],
  sensitivityLevelsLoading: false,
  mainDrivers: [],
  mainDriversLoading: false,
  assessmentCreators: [],
  assessmentCreatorsLoading: false,
  underwritingSources: [],
  underwritingSourcesLoading: false,
  businessUnits: [],
  businessUnitsLoading: false,
  legalEntities: [],
  legalEntitiesLoading: false,
  selectedDialogFilter: {
    country: [],
    countryIntersection: false,
    industry: [],
    company: [],
    project: [],
    dealId: [],
    underwritingSource: [],
    sensitivityLevel: [],
    mainDriver: [],
    createdById: [],
    businessUnit: [],
    legalEntity: [],
    dateFrom: undefined,
    dateTo: undefined,
  },
};

function createLoadMethod<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  store: any,
  storeProperty: keyof AllAssessmentsFilterDataState,
  apiCall: () => Observable<T[]>,
  loading?: keyof AllAssessmentsFilterDataState
) {
  return rxMethod<void>(
    pipe(
      exhaustMap(() => {
        if ((store[storeProperty]() as T[]).length > 0) {
          return EMPTY; // If data is already loaded, do nothing
        }
        if (loading) {
          patchState(store, { [loading]: true });
        }
        return apiCall().pipe(
          tapResponse({
            next: data => {
              patchState(store, { [storeProperty]: data });
            },
            error: () => {
              // error snackbar handled in service
            },
            finalize: () => {
              if (loading) {
                patchState(store, { [loading]: false });
              }
            },
          })
        );
      })
    )
  );
}

export function withAllAssessmentFilterDataFeature() {
  return signalStoreFeature(
    withState(initialState),
    withMethods(
      (
        store,
        dictionaryDataApiService = inject(DictionaryDataApiService),
        assessmentApiService = inject(AssessmentsApiService),
        sensitivityLevelApiService = inject(SensitivityLevelApiService),
        mainDriverApiService = inject(MainDriverApiService),
        userApiService = inject(UserApiService)
      ) => {
        const loadCountries = createLoadMethod(store, 'countries', () => dictionaryDataApiService.getCountries$(), 'countriesLoading');

        const loadIndustryNames = createLoadMethod(
          store,
          'industryNames',
          () => assessmentApiService.getAssessmentIndustryNames$(),
          'industryNamesLoading'
        );

        const loadCompanyNames = createLoadMethod(
          store,
          'companyNames',
          () => assessmentApiService.getAssessmentCompanyNames$(),
          'companyNamesLoading'
        );

        const loadProjectNames = createLoadMethod(
          store,
          'projectNames',
          () => assessmentApiService.getAssessmentProjectNames$(),
          'projectNamesLoading'
        );

        const loadDealIds = createLoadMethod(store, 'dealIds', () => assessmentApiService.getDealIds$(), 'dealIdsLoading');

        const loadUnderwritingSources = createLoadMethod(
          store,
          'underwritingSources',
          () => assessmentApiService.getUnderwritingSources$(),
          'underwritingSourcesLoading'
        );

        const loadSensitivityLevels = createLoadMethod(
          store,
          'sensitivityLevels',
          () => sensitivityLevelApiService.getSensitivityLevels$().pipe(map(levels => levels.map(level => level.name))),
          'sensitivityLevelsLoading'
        );

        const loadMainDrivers = createLoadMethod(
          store,
          'mainDrivers',
          () => mainDriverApiService.getMainDrivers$().pipe(map(drivers => drivers.map(driver => driver.text))),
          'mainDriversLoading'
        );

        const loadAssessmentCreators = createLoadMethod(
          store,
          'assessmentCreators',
          () => userApiService.getAssessmentCreators$(),
          'assessmentCreatorsLoading'
        );

        const loadBusinessUnits = createLoadMethod(
          store,
          'businessUnits',
          () => dictionaryDataApiService.getBusinessUnits$(),
          'businessUnitsLoading'
        );

        const loadLegalEntities = createLoadMethod(
          store,
          'legalEntities',
          () => dictionaryDataApiService.getLegalEntities$(),
          'legalEntitiesLoading'
        );

        const updateSelectedFilter = (filterData: AssessmentsDialogFilter) => {
          patchState(store, { selectedDialogFilter: filterData });
        };

        const resetDialogFilter = () => {
          patchState(store, {
            selectedDialogFilter: { ...initialState.selectedDialogFilter },
          });
        };

        return {
          loadCountries,
          loadIndustryNames,
          loadCompanyNames,
          loadProjectNames,
          loadDealIds,
          loadUnderwritingSources,
          loadSensitivityLevels,
          loadMainDrivers,
          loadAssessmentCreators,
          loadBusinessUnits,
          loadLegalEntities,
          updateSelectedFilter,
          resetDialogFilter,
        };
      }
    )
  );
}
